import React, { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useAnalysisHistory } from '@/hooks/useAnalysisHistory';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import Filters from '@/components/analysisHistory/Filters';
import AnalysisTable from '@/components/analysisHistory/AnalysisTable';
import Pagination from '@/components/analysisHistory/Pagination';

const AnalysisHistory = () => {
    const { user } = useAuth();
    const [downloadingId, setDownloadingId] = useState(null);
    const {
        analyses,
        loading,
        error,
        currentPage,
        totalPages,
        sortConfig,
        filters,
        setCurrentPage,
        setSortConfig,
        setFilters,
        refetch, // Added refetch function from the hook
    } = useAnalysisHistory(user?.id);

    const handleDownload = async (analysis) => {
        if (downloadingId) return;

        setDownloadingId(analysis.id);
        try {
            const { data, error } = await supabase
                .storage
                .from('analysis-files')
                .download(analysis.csv_result_url);

            if (error) throw error;

            const blob = new Blob([data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const date = new Date(analysis.timestamp).toISOString().split('T')[0];
            a.download = `analysis_results_${date}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error('Error downloading file:', err);
            alert('Failed to download file. Please try again.');
        } finally {
            setDownloadingId(null);
        }
    };

    const handleSort = (column) => {
        setSortConfig(prev => ({
            column,
            direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    return (
        <div className="md:p-6 space-y-6">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-xl">Analysis History</CardTitle>
                    <div className='hidden lg:inline-block'>
                        <Filters filters={filters} setFilters={setFilters} />
                    </div>
                </CardHeader>
                <CardContent>
                    {error && (
                        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-lg mb-6" role="alert">
                            <p className="text-sm font-medium">{error}</p>
                        </div>
                    )}

                    <>
                        <AnalysisTable
                            analyses={analyses}
                            sortConfig={sortConfig}
                            handleSort={handleSort}
                            handleDownload={handleDownload}
                            downloadingId={downloadingId}
                            loading={loading}
                        />
                        {!loading && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                setCurrentPage={setCurrentPage}
                                totalEntries={totalPages * 10}
                                entriesPerPage={10}
                            />
                        )}
                    </>
                </CardContent>
            </Card>
        </div>
    );
};

export default AnalysisHistory;